import {isValidationFailedResponse, resetPassword, ValidationFailures} from '@/api';
import ValidatedFormGroup from '@/components/ValidatedFormGroup.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, Classes, Dialog, InputGroup} from '@blueprintjs/core';
import {ChangeEventHandler, EventHandler, ReactElement, SyntheticEvent, useCallback, useState} from 'react';

interface Props {
	onClose: () => void,
}

export default function ForgotPasswordDialog({onClose}: Props): ReactElement {
	const [processing, setProcessing] = useState(false);
	const [validationFailures, setValidationFailures] = useState<ValidationFailures | null>(null);

	const [email, setEmail] = useState('');

	const onEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setEmail(event.currentTarget.value);
	}, []);

	const onSubmit: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		if (processing)
			return;

		setProcessing(true);

		try {
			await resetPassword(email);
		} catch (error) {
			if (isValidationFailedResponse(error)) {
				Toaster.error(error.error.message);
				setValidationFailures(error.error.context.failures);
			} else
				ToastErrors.fromError(error);

			return;
		} finally {
			setProcessing(false);
		}

		Toaster.success('Your request has been submitted. Check your email inbox for instructions.');
		onClose();
	}, [processing, email, onClose]);

	return (
		<Dialog isOpen={true} onClose={onClose} title="Password Reset">
			<form onSubmit={onSubmit}>
				<div className={Classes.DIALOG_BODY}>
					<p>
						To reset your password, enter your account's email address in the box below. If an account
						exists with that email address, an email containing password reset instructions will be sent
						shortly.
					</p>

					<br />

					<ValidatedFormGroup labelFor="emailAddress" failures={validationFailures}>
						<InputGroup value={email} onChange={onEmailChange} autoFocus={true} />
					</ValidatedFormGroup>
				</div>

				<div className={Classes.DIALOG_FOOTER}>
					<div className={Classes.DIALOG_FOOTER_ACTIONS}>
						<Button text="Cancel" onClick={onClose} disabled={processing} />
						<Button type="submit" intent="primary" text="Submit" loading={processing} />
					</div>
				</div>
			</form>
		</Dialog>
	);
}
